import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Container, Breadcrumbs, Link, IconButton, CircularProgress, Tooltip, useTheme, Divider, Avatar } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from '../../MiniBot';
import { useAuth } from '../../../helpers/AuthProvider';

// --- Interfaces ---
interface ApplicationData {
    id: string;
    applicationName: string;
}

interface SolutionOwnerData {
    id: string;
    name: string;
    googleProfilePicture: string;
}
// --- Helper Component ---
const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null }> = ({ label, value }) => {
    const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}</Typography>
        </Box>
    );
};

interface RAGDetails {
    projectId: string;
    databaseType: string;
    embeddingModelId: string;
    chunkingStrategy: string;
    chunkSize: string;
    chunkOverlap: string;
    region: string;
    network: string;
        subnets: string;
        serviceAccount: string;
        databaseConnectionSecret: string;
}

interface RAGData {
    id: string;
    name: string;
    solutionType: string;
    solutionCategoryType: string;
    reasonForRejection: string;
    cloudProvider: 'gcp' | 'aws' | 'azure' | string;
    environment: string;
    details: {
        rag: RAGDetails;
    };
    status: string;
    solutionOwner: SolutionOwnerData;
    application?: ApplicationData;
    solutionApprover?: SolutionOwnerData; 
}

const RAGDetailsPageContent: React.FC = () => {
    const navigate = useNavigate();
    const {id}  = useParams();
    const [rag, setRag] = useState<RAGData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();

    useEffect(() => {
        const fetchRAGData = async () => {
            if (!id) {
                setIsLoading(false);
                setError("RAG ID is missing from URL.");
                return;
            }

            setIsLoading(true);
            setError(null);
            try {
                const query = `
                    query GetRAG($getRagId: ID!) {
                      getRAG(id: $getRagId) {
                        id
                        name
                        solutionType
                        solutionCategoryType
                        cloudProvider
                        environment
                        status
                        details {
                          rag {
                            projectId
                            databaseType
                            embeddingModelId
                            chunkingStrategy
                            chunkSize
                            chunkOverlap
                            region
                            network
                            subnets
                            serviceAccount
                            databaseConnectionSecret
                          }
                        }
                        solutionOwner {
                          id
                          name
                        }
                        solutionApprover {
                          id
                          name
                          googleProfilePicture
                        }
                        application {
                          id
                          applicationName
                        }
                      }
                    }
                `;
                const response = await queryGraphql(query, { getRagId: id });
                
                if (response.getRAG) {
                    setRag(response.getRAG);
                } else if (response.errors && response.errors.length > 0) {
                    setError(response.errors[0].message || "Error fetching data.");
                    setRag(null);
                } else {
                    setError("RAG not found or unexpected response structure.");
                    setRag(null);
                }
            } catch (err: any) {
                console.error("Error fetching RAG data:", err);
                setError(err.message || "Failed to fetch RAG details.");
                setRag(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchRAGData();
    }, [id]);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography sx={{ ml: 2 }}>Loading RAG details...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Typography color="error" sx={{p:2}}>{error}</Typography>;
    }

    if (!rag) {
        return <Typography sx={{p:2}}>No RAG data available. Ensure the ID is correct and the API is reachable.</Typography>;
    }

    const resourceOwnerName = rag.solutionOwner.name;
    
    const projectIdLabel = rag.cloudProvider === 'aws' ? 'Account ID' : rag.cloudProvider === 'azure' ? 'Subscription ID' : 'Project ID';

    const getStatusBackgroundColor = (status: string | undefined): string => {
        if (!status) return "#F8F8F8"; 
        const lowerStatus = status.toLowerCase();
        if (lowerStatus === "active" || lowerStatus === "approved") {
            return "#12B76A10";
        } else if (lowerStatus === "rejected") {
            return "#F0443810";
        } else if (lowerStatus === "pending") {
            return "#FDB02210";
        }
        return "#F8F8F8";
    };

    return (
        <Container maxWidth={false} sx={{ mt: 2, mb: 2, pl: { xs: 2, sm: 3 }, pr: {xs: 2, sm: 3} }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "14px", color: "gray" }}>
                <Link underline="hover" color="inherit" href="/aisolutions/rag" sx={{fontFamily:'Gilroy-regular'}} onClick={(e) => { e.preventDefault(); navigate('/aisolutions/rag'); }}>RAG Solutions</Link>
                <Typography color="text.primary" sx={{fontSize: "14px", fontFamily:'Gilroy-Medium'}}>RAG Details</Typography>
            </Breadcrumbs>

            <Box display="flex" alignItems="center" mb={3}>
                <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate('/aisolutions/rag')}>
                    <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <Typography variant="h5" component="h1" sx={{ fontFamily:'Gilroy-Bold',fontSize: "18px"}}>RAG Details</Typography>
            </Box>

            <Box sx={{mx:0, boxShadow: "0px 3px 6px #10101029", borderRadius: "8px" }}>
                <Box sx={{
                    p: 2.5,
                    bgcolor: getStatusBackgroundColor(rag.status),
                    mb: 0,
                    mx: 0,
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                }}>
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <Typography variant="h6" component="h2" sx={{ fontFamily: 'Gilroy-Bold', fontSize: '20px' }}>
                                {rag.name}
                            </Typography>
                            {user.roles?.some((e: string) => ["cluster_owner"].includes(e)) && rag.status === "pending" && (
                                <IconButton sx={{ p: 0.5 }} edge="end" aria-label="edit" color="inherit" onClick={() => navigate(`/aisolutions/rag/edit/${id}`)}>
                                    <img width="20px" height="20px" src="/assets/edit-icon.svg" alt="Edit"/>
                                </IconButton>
                            )}
                        </Box>
                        <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#666666' }}>
                            Resource Owner: {resourceOwnerName}
                        </Typography>
                    </Box>
                </Box>

                <Grid container spacing={3} sx={{p:3, pt: 2.5 }}>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>{projectIdLabel}</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{rag.details.rag.projectId}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Cloud Provider</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333', textTransform:'uppercase'}}>{rag.cloudProvider}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Region</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{rag.details.rag.region}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Environment</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{rag.environment}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Application Name</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{rag.application?.applicationName || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Category Type</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{rag.solutionCategoryType}</Typography>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 0, p: 2.5, borderTop: "1px solid #e0e0e0" }}>
                    <Typography component="h3" sx={{ fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
                        RAG Configuration
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Database Type</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{rag.details.rag.databaseType}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Embedding Model ID</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{rag.details.rag.embeddingModelId}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Chunking Strategy</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{rag.details.rag.chunkingStrategy}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Chunk Size</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{rag.details.rag.chunkSize}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Chunk Overlap</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{rag.details.rag.chunkOverlap}</Typography>
                        </Grid>
                        {/* {(rag.status === 'active' || rag.status === 'rejected') && <Grid item xs={12} sm={6} md={3}>
                                                        <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">Actioned by</Typography>
                                                        <Box display="flex" alignItems="center" mt={0.5}>
                                                            <Avatar sx={{ height: "24px", width: "24px", mr: 1 }} src={rag.solutionApprover?.googleProfilePicture} alt={rag.solutionApprover?.name?.charAt(0)}/>
                                                            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" noWrap> {rag.solutionApprover?.name } </Typography>
                                                        </Box>
                                                    </Grid>}
                                                    {rag.status === 'rejected' && 
                                                        <Grid item xs={12} sm={6} md={9}><DetailItem label="Reason for Rejection" value={rag.reasonForRejection} /></Grid>
                                                    } */}
                    </Grid>
                </Box>
            </Box>
            {rag.status === 'rejected' && (
                                                        <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                                        <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
                                                        <Divider sx={{ marginY: 2 }} />
                                                        <DetailItem label="Reason for Rejection" value={rag.reasonForRejection} />
                                                        </Box>
                                                )}

            <Box sx={{ mt: 0, p: 0, borderTop: "1px solid #e0e0e0" }}>

                {rag.status === 'active' && (
                                             <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                                <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Cloud Infra Details</Typography>
                                                <Divider sx={{ marginY: 2 }} />
                                                <Grid container spacing={2.5}>
                                                    <Grid item xs={12} sm={6} md={3}><DetailItem label="Network" value={rag.details.rag.network} /></Grid>
                                                    <Grid item xs={12} sm={6} md={3}><DetailItem label="Subnets" value={rag.details.rag.subnets} /></Grid>
                                                    {/* <Grid item xs={12} sm={6} md={3}><DetailItem label="Database Connection Secret" value={rag.details.rag.databaseConnectionSecret} /></Grid> */}
                                                    <Grid item xs={12} sm={6} md={3}><DetailItem label="Service Account" value={rag.details.rag.serviceAccount} /></Grid>
                                                </Grid>
                                             </Box>
                                        )}
                                        </Box>

                                        
        </Container>
    );
};


const RAGDetailsPage: React.FC = () => {
    const theme = useTheme();
    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') {
            localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
        }
    };

    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer
                index={"aisolutions-rag"} // Updated index for drawer
                onStateChange={handleDrawerStateChange}
            />
            <MiniBot/>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    marginLeft: {xs: 0, sm: `${currentMarginLeft}px`},
                }}
            >
                <RAGDetailsPageContent />
            </Box>
        </Box>
    );
};

export default RAGDetailsPage;